import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Toast, type ToastTone } from "./primitives";

export interface ToastOptions {
  tone?: ToastTone;
  title: ReactNode;
  message?: ReactNode;
  action?: { label: string; fn: () => void };
  durationMs?: number;
}

const ToastContext = createContext<(options: ToastOptions) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

/** One toast at a time, bottom centre of the main area, as the prototype showed them. */
export function ToastProvider({ children, offsetLeft = 0 }: { children: ReactNode; offsetLeft?: number }) {
  const [toast, setToast] = useState<ToastOptions | null>(null);
  const timer = useRef<number | null>(null);
  const show = useCallback((options: ToastOptions) => {
    if (timer.current) window.clearTimeout(timer.current);
    setToast(options);
    timer.current = window.setTimeout(() => setToast(null), options.durationMs ?? (options.action ? 8000 : 5000));
  }, []);
  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: `calc(50% + ${offsetLeft / 2}px)`, transform: "translateX(-50%)", zIndex: 120 }}>
          <Toast
            tone={toast.tone}
            title={toast.title}
            message={toast.message}
            action={toast.action && (
              <button type="button" onClick={() => { toast.action!.fn(); setToast(null); }} style={{ height: 30, padding: "0 12px", borderRadius: 99, border: "1px solid rgba(255,255,255,.3)", background: "transparent", color: "#fff", font: "700 13px var(--font-body)", cursor: "pointer" }}>
                {toast.action.label}
              </button>
            )}
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </ToastContext.Provider>
  );
}
